/**
 * Job preparation helpers — pure transformation functions extracted from API orchestration.
 */

import type { Job, Grower, Company } from "../../models";
import type { JobFlags } from "../../utilities/types";

export interface NewSalesperson {
	name: string;
	phone: string;
}

/**
 * Deduplicates new salesperson entries from a batch of jobs.
 * Matches on case-insensitive name + trimmed phone.
 */
export function deduplicateNewSalespersons(jobs: Partial<Job>[]): NewSalesperson[] {
	const seen = new Map<string, NewSalesperson>();
	for (const job of jobs) {
		const sp = job.newSalesperson;
		if (!sp?.name || sp.name.trim() === "") continue;
		const key = `${sp.name.trim().toLowerCase()}|${(sp.phone ?? "").trim()}`;
		if (!seen.has(key)) {
			seen.set(key, { name: sp.name, phone: sp.phone ?? "" });
		}
	}
	return Array.from(seen.values());
}

/**
 * Splits a full name into first and last name components.
 * First word becomes firstName, remainder becomes lastName.
 */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
	const parts = fullName.trim().split(" ");
	return {
		firstName: parts.at(0) || "",
		lastName: parts.slice(1).join(" ") || "",
	};
}

/**
 * Builds the jobFlags array for a job being created.
 */
export function buildJobFlags(existingFlags?: JobFlags[], source?: JobFlags, isAdmin?: boolean): JobFlags[] {
	const flags: JobFlags[] = existingFlags ? [...existingFlags] : [];
	if (source) flags.push(source);
	if (source == "Created Portal API Agrian") flags.push("Agrian" as JobFlags);
	if (isAdmin) flags.push("ROGO Created" as JobFlags);
	return flags;
}

/**
 * Maps external grower IDs to jobs by matching externalGrower.id to grower.externalId.
 * Mutates job.growerIds in place.
 */
export function mapExternalGrowersToJobs(jobs: Partial<Job>[], allGrowers: Grower[]): void {
	for (const job of jobs) {
		const grower = allGrowers.find((g) => g.externalId === job.externalGrower?.id);
		if (grower) job.growerIds = [grower.id];
	}
}

/**
 * Filters external growers to find those that don't exist in the system yet.
 * Returns growers whose id does not match any existingGrower.externalId.
 */
export function filterNewExternalGrowers(
	externalGrowers: Array<{ id: string; name: string }>,
	existingGrowers: Grower[],
): Array<{ id: string; name: string }> {
	return externalGrowers.filter((g) => !existingGrowers.some((eg) => eg.externalId === g.id));
}

/**
 * Sorts companies with "Rogo AG" and "ROGO Software" pinned to the top,
 * then alphabetically by name.
 */
export function sortCompanies<T extends Pick<Company, "name">>(companies: T[]): T[] {
	const pinned = new Set(["Rogo AG", "ROGO Software"]);
	return [...companies].sort((a, b) => {
		const aPin = pinned.has(a.name);
		const bPin = pinned.has(b.name);
		if (aPin && !bPin) return -1;
		if (!aPin && bPin) return 1;
		return a.name.localeCompare(b.name);
	});
}
